/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable camelcase */
import axios from 'axios'

const apiBase = '/api'

export interface BaseRegisterAction {
  action: 'register' | 'resend'
}

export interface RegisterUser extends BaseRegisterAction {
  email: string;
  given_name: string;
  family_name: string;
  personal_use: boolean;
  marketing_optout: boolean;
  address: {
    country: string;
    region?: string;
  }
  password: string;
  company?: string;
  abn?: string;
  csrf_token: string;
}

export interface SearchCompany {
  // Ex: "Abn": "39838003245", "AbnStatus": "0000000001", "IsCurrent": true,
  //     "Name": "#LIMITLESS", "NameType": "Business Name", "Postcode": "3037",
  //     "Score": 94, "State": "VIC"
  Abn: string;
  AbnStatus: string;
  IsCurrent: boolean;
  Name: string;
  NameType: string;
  Postcode: string;
  Score: number;
  State: string;
}

export interface SimpleResponse {
  status?: string;
  error_code?: string;
  error_detail?: string;
}

export interface SearchCompanyResponse extends SimpleResponse {
  Message: string;
  Names: Array<SearchCompany>;
}

export interface SearchABNResponse extends SimpleResponse {
  // "Abn": "65386935864", "AbnStatus": "Active", "AbnStatusEffectiveFrom": "2006-01-19",
  // "Acn": "", "AddressDate": "2006-04-27", "AddressPostcode": "2000",
  // "AddressState": "NSW", "BusinessName": [], "EntityName": "B JANCIC & J JANCIC",
  // "EntityTypeCode": "FPT", "EntityTypeName": "Family Partnership", "Gst": null,
  // "Message": ""
  Message: string;
  Abn: string;
  AbnStatus: string;
  AbnStatusEffectiveFrom: string; // YYYY-MM-DD
  Acn: string;
  AddressDate: string; // YYYY-MM-DD
  AddressPostcode: string;
  AddressState: string;
  BusinessName: Array<string>;
  EntityName: string;
  EntityTypeCode: string;
  EntityTypeName: string;
  Gst: string | null;
}

class BaseAPI {
  api: string = apiBase
  getUrl (endPoint: string, customerId?: number | string, vpnuserId?: number | string) {
    return '/' + endPoint + '/' + (customerId !== undefined ? customerId.toString() : '') + (customerId !== undefined && vpnuserId !== undefined ? ('/' + vpnuserId.toString()) : '')
  }

  handleError (e: {message?: string, response?:{status?: number}}) {
    if ((e.response && e.response.status === 302) ||
        (!e.response && e.message === 'Network Error')) {
      // Likely an auth expiration, reload this page
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg) {
          reg.unregister().then(() => { window.location.reload() })
        } else {
          window.location.reload()
        }
      })
    }
  }

  getRaw<T> (url: string): Promise<T | null> {
    return new Promise<T | null>((resolve) => {
      axios.get(url).then(resp => {
        resolve(resp.data as T)
      }).catch((e) => {
        this.handleError(e)
        resolve(null)
      })
    })
  }

  get<T> (endPoint: string): Promise<T | null> {
    return this.getRaw<T>(this.api + endPoint)
  }

  deleteRaw<T> (url: string): Promise<T | null> {
    return new Promise<T | null>((resolve) => {
      axios.delete(url).then(resp => {
        resolve(resp.data as T)
      }).catch((e) => {
        this.handleError(e)
        resolve(null)
      })
    })
  }

  delete<T> (endPoint: string): Promise<T | null> {
    return this.deleteRaw<T>(this.api + endPoint)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deleteParamsRaw<T> (url: string, params: any): Promise<T | null> {
    return new Promise<T | null>((resolve) => {
      axios.delete(url, { params: params }).then(resp => {
        resolve(resp.data as T)
      }).catch((e) => {
        this.handleError(e)
        resolve(null)
      })
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deleteParams<T> (endPoint: string, params: any): Promise<T | null> {
    return this.deleteParamsRaw(this.api + endPoint, params)
  }

  deleteData<T> (endPoint: string, data: string): Promise<T | null> {
    return new Promise<T | null>((resolve) => {
      axios.delete(this.api + endPoint, { data: data }).then(resp => {
        resolve(resp.data as T)
      }).catch((e) => {
        this.handleError(e)
        resolve(null)
      })
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getParamRaw<T> (url: string, params: any): Promise<T | null> {
    return new Promise<T | null>((resolve) => {
      axios.get(url, { params: params }).then(resp => {
        resolve(resp.data as T)
      }).catch((e) => {
        this.handleError(e)
        resolve(null)
      })
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getParams<T> (endPoint: string, params: any): Promise<T | null> {
    return this.getParamRaw<T>(this.api + endPoint, params)
  }

  patch<T> (endPoint: string, data: string): Promise<T | null> {
    return new Promise<T | null>((resolve) => {
      axios.patch(this.api + endPoint, data).then(resp => {
        resolve(resp.data as T)
      }).catch((e) => {
        this.handleError(e)
        resolve(null)
      })
    })
  }

  postRaw<T> (url: string, data: string | Record<string, string> | FormData): Promise<T | null> {
    return new Promise<T | null>((resolve) => {
      axios.post(url, data).then(resp => {
        resolve(resp.data as T)
      }).catch((e) => {
        this.handleError(e)
        resolve(null)
      })
    })
  }

  post<T> (endPoint: string, data: string | Record<string, string> | FormData): Promise<T | null> {
    return this.postRaw<T>(this.api + endPoint, data)
  }

  postFile<T> (url: string, fd: FormData): Promise<T | null> {
    return new Promise<T | null>((resolve) => {
      axios.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(resp => {
        resolve(resp.data as T)
      }).catch((e) => {
        this.handleError(e)
        resolve(null)
      })
    })
  }

  put<T> (endPoint: string, data: string): Promise<T | null> {
    return new Promise<T | null>((resolve) => {
      axios.put(this.api + endPoint, data).then(resp => {
        resolve(resp.data as T)
      }).catch((e) => {
        this.handleError(e)
        resolve(null)
      })
    })
  }
}

export class PublicAPIImpl extends BaseAPI {
  getSearchCompany (val: string) {
    return this.getParamRaw<SearchCompanyResponse>('/search/company', { val: val })
  }

  getSearchABN (val: string) {
    return this.getParamRaw<SearchABNResponse>('/search/abn', { val: val })
  }

  postRegister (val: BaseRegisterAction) {
    return this.postRaw<SimpleResponse>('/register', JSON.stringify(val))
  }
}

const PublicAPI = new PublicAPIImpl()

export default PublicAPI
